import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Upload, Loader2 } from "lucide-react";
import { useContent, useContentRefresh } from "../content-context";
import { chunkedUpload, isVideoFile } from "../utils/mediaUpload";

import API from "../api";

export const GALLERY_CATEGORIES = [
  "The Island & Clew Bay",
  "The Lodge & Accommodation",
  "Beach Sauna & Hot Tub",
  "Island Adventures",
  "Celebrations & Events",
  "Traitors Island",
];

export default function GalleryManager() {
  const { galleryPage } = useContent();
  const refresh = useContentRefresh();
  const [rows, setRows] = useState(() => JSON.parse(JSON.stringify(galleryPage.items || [])));
  const [busyIdx, setBusyIdx] = useState(-1);
  const [prog, setProg] = useState(null);
  const [sugIdx, setSugIdx] = useState(-1);
  const [saving, setSaving] = useState(false);
  const fileRefs = useRef({});

  const update = (i, key, val) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [key]: val } : row)));
  const remove = (i) => setRows((r) => r.filter((_, j) => j !== i));
  const add = () => setRows((r) => [...r, { src: "", alt: "", category: GALLERY_CATEGORIES[0] }]);
  const move = (i, dir) =>
    setRows((r) => {
      const j = i + dir;
      if (j < 0 || j >= r.length) return r;
      const copy = [...r];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const suggest = async (i, srcOverride) => {
    const url = srcOverride || rows[i]?.src;
    if (!url) {
      toast.error("Upload or choose a photo first");
      return;
    }
    setSugIdx(i);
    try {
      const res = await fetch(`${API}/api/admin/alt-suggest`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not describe that photo");
      update(i, "alt", data.alt);
      toast.success("Description written — remember to save");
    } catch (err) {
      toast.error(err.message || "Could not describe that photo");
    } finally {
      setSugIdx(-1);
    }
  };

  const upload = async (i, file) => {
    setBusyIdx(i);
    try {
      const video = isVideoFile(file);
      let data;
      if (video || file.size > 20 * 1024 * 1024) {
        data = await chunkedUpload(API, file, (pct) => setProg({ i, pct }));
        setProg({ i, pct: 100 });
      } else {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API}/api/admin/upload`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        data = await res.json();
        if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
      }
      if (video) update(i, "type", "video");
      update(i, "src", data.url);
      toast.success(video ? "Video uploaded — remember to save" : "Photo uploaded — asking AI to describe it…");
      if (!video) await suggest(i, data.url);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusyIdx(-1);
      setProg(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const current = await fetch(`${API}/api/content`).then((r) => r.json());
      const base = current.content || {};
      const clean = rows.filter((r) => r.src);
      const next = { ...base, galleryPage: { ...galleryPage, items: clean } };
      const res = await fetch(`${API}/api/admin/content`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(typeof (err && err.detail) === "string" ? err.detail : "Could not save. Please try again.");
      }
      await refresh();
      setRows(clean);
      toast.success("Gallery saved — the website is updated.");
    } catch (err) {
      toast.error(err.message || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-2 w-full border-b border-ivory/25 bg-transparent py-2 text-sm font-light text-ivory outline-none focus:border-gold";

  return (
    <div className="mt-8" data-testid="gallery-manager">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl text-ivory">Gallery photographs & videos</h2>
          <p className="mt-3 max-w-xl text-sm font-light leading-relaxed text-ivory/70">
            Add, replace, reorder or remove the photographs shown on the Gallery page. Upload photos from your
            phone or computer — they are stored safely and appear on the website the moment you save.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={add}
            data-testid="gallery-add-button"
            className="flex items-center gap-2 border border-ivory/25 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2"
          >
            <Plus className="h-4 w-4" /> Add photo
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="gallery-save-button"
            className="bg-gold px-8 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save gallery"}
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-5">
        {rows.map((row, i) => (
          <div key={i} className="border border-ivory/15 bg-navy-2 p-5" data-testid={`gallery-row-${i + 1}`}>
            <div className="flex flex-col gap-5 md:flex-row">
              <div className="flex w-full shrink-0 items-start gap-3 md:w-44">
                {row.src ? (
                  row.type === "video" ? (
                    <video src={row.src} muted playsInline className="h-24 w-36 border border-ivory/20 object-cover" />
                  ) : (
                    <img src={row.src} alt="" className="h-24 w-36 border border-ivory/20 object-cover" />
                  )
                ) : (
                  <div className="flex h-24 w-36 items-center justify-center border border-dashed border-ivory/25 text-[10px] uppercase tracking-widest2 text-ivory/40">
                    No image yet
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={(el) => (fileRefs.current[i] = el)}
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && upload(i, e.target.files[0])}
                    data-testid={`gallery-upload-input-${i + 1}`}
                  />
                  <button
                    onClick={() => fileRefs.current[i].click()}
                    disabled={busyIdx === i}
                    data-testid={`gallery-upload-button-${i + 1}`}
                    className="flex items-center gap-2 border border-gold/60 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy disabled:opacity-60"
                  >
                    {busyIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    Upload
                  </button>
                  {busyIdx === i && prog && prog.i === i && (
                    <div className="h-1 w-24 bg-ivory/20" data-testid={`gallery-upload-progress-${i + 1}`}>
                      <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${prog.pct}%` }} />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => move(i, -1)} aria-label="Move earlier" data-testid={`gallery-up-${i + 1}`} className="border border-ivory/25 px-2.5 py-1 text-[10px] text-ivory/70 hover:border-gold hover:text-gold-2">
                      ↑
                    </button>
                    <button onClick={() => move(i, 1)} aria-label="Move later" data-testid={`gallery-down-${i + 1}`} className="border border-ivory/25 px-2.5 py-1 text-[10px] text-ivory/70 hover:border-gold hover:text-gold-2">
                      ↓
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Category</label>
                  <select
                    value={row.category || GALLERY_CATEGORIES[0]}
                    onChange={(e) => update(i, "category", e.target.value)}
                    data-testid={`gallery-category-${i + 1}`}
                    className={`${inputCls} bg-navy-2`}
                  >
                    {GALLERY_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">
                    Description (shows with the photo)
                  </label>
                  <div className="flex items-end gap-2">
                    <input
                      value={row.alt || ""}
                      onChange={(e) => update(i, "alt", e.target.value)}
                      data-testid={`gallery-alt-${i + 1}`}
                      placeholder="e.g. Guests enjoying the beach sauna"
                      className={`${inputCls} flex-1`}
                    />
                    <button
                      onClick={() => suggest(i)}
                      disabled={sugIdx === i}
                      title="Write this description with AI"
                      aria-label={`Write description ${i + 1} with AI`}
                      data-testid={`gallery-suggest-${i + 1}`}
                      className="mb-1 flex h-9 w-9 shrink-0 items-center justify-center border border-gold/60 text-gold-2 transition-colors hover:bg-gold hover:text-navy disabled:opacity-60"
                    >
                      {sugIdx === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Photo or video address</label>
                  <input
                    value={row.src || ""}
                    onChange={(e) => update(i, "src", e.target.value)}
                    data-testid={`gallery-src-${i + 1}`}
                    placeholder="/images/real/… or upload a file above"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="flex items-start">
                <button
                  onClick={() => remove(i)}
                  data-testid={`gallery-remove-${i + 1}`}
                  aria-label={`Remove photo ${i + 1}`}
                  className="flex h-10 w-10 items-center justify-center border border-ivory/25 text-ivory/70 transition-colors hover:border-red-400 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="gallery-save-button-bottom"
        className="mt-8 bg-gold px-8 py-3.5 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save gallery"}
      </button>
    </div>
  );
}
